/**
 * @fileoverview Profile Detection Component
 * 
 * Checks if authenticated user has a creator profile and routes them appropriately.
 * Shows loading state during check, handles errors gracefully, and provides manual
 * navigation options if automatic detection fails.
 * 
 * Requirements:
 * - 2.1: Redirect users without profiles to onboarding workflow
 * - 3.2: Redirect to onboarding if no profile exists
 * - 3.3: Redirect to dashboard if profile exists
 * - 3.4: Handle API errors gracefully without blocking login
 * - 3.5: Display loading indicator during profile check
 */

import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button, Paper, Alert } from '@mui/material';
import { useProfileDetection } from '../hooks/useProfileDetection';

export interface ProfileDetectionProps {
  /**
   * Custom loading message to display during profile check
   */
  loadingMessage?: string;
  
  /**
   * Callback invoked when profile check completes successfully
   * @param hasProfile - Whether the user has a creator profile
   */
  onProfileChecked?: (hasProfile: boolean) => void;
  
  /**
   * Custom children to render during loading state
   */
  children?: React.ReactNode;
  
  /**
   * MVP RESTRICTION: Redirect to dashboard if user already has a profile
   * 
   * When true, inverts the normal behavior:
   * - If user HAS profile -> redirect to /dashboard
   * - If user has NO profile -> render children (onboarding)
   * 
   * This is used to prevent users from accessing /onboarding when they
   * already have a profile, enforcing the MVP one-profile-per-user limit.
   * 
   * FUTURE: Remove this when implementing multi-profile support.
   * The underlying data model already supports multiple profiles per user.
   * 
   * @default false
   */
  redirectIfHasProfile?: boolean;
}

/**
 * Component that detects user's profile status and handles routing
 */
export function ProfileDetection({
  loadingMessage = 'Checking your profile...',
  onProfileChecked,
  children,
  redirectIfHasProfile = false,
}: ProfileDetectionProps) {
  const { isLoading, hasProfile, redirectTo, error, retry } = useProfileDetection();

  // Call onProfileChecked callback when profile check completes
  useEffect(() => {
    if (!isLoading && hasProfile !== null && onProfileChecked) {
      onProfileChecked(hasProfile);
    }
  }, [isLoading, hasProfile, onProfileChecked]);

  // Show loading state
  if (isLoading) {
    // Render custom children if provided
    if (children) {
      return <>{children}</>;
    }

    // Default loading UI
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} role="status" />
        <Typography variant="body1" color="text.secondary">
          {loadingMessage}
        </Typography>
      </Box>
    );
  }

  // Handle error state - show error but provide manual navigation
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 600,
            width: '100%',
          }}
        >
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Profile Check Failed
            </Typography>
            <Typography variant="body2">
              {error}
            </Typography>
          </Alert>

          <Box sx={{ mb: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={retry}
              fullWidth
              sx={{ mb: 2 }}
            >
              Retry
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            You can still navigate manually:
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => { window.location.href = '/onboarding'; }}
              fullWidth
            >
              Go to Onboarding
            </Button>
            <Button
              variant="outlined"
              onClick={() => { window.location.href = '/dashboard'; }}
              fullWidth
            >
              Go to Dashboard
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            If you're a new user, start with Onboarding. If you've already set up your profile, go to Dashboard.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // MVP RESTRICTION: Handle inverted logic for onboarding route protection
  // When redirectIfHasProfile=true, we're protecting the onboarding page
  // from users who already have profiles (one-profile-per-user MVP limit)
  if (redirectIfHasProfile) {
    // User has profile -> redirect to dashboard (prevent duplicate profile creation)
    if (redirectTo === 'dashboard') {
      return <Navigate to="/dashboard" replace />;
    }
    // User has no profile -> render children (allow onboarding)
    if (redirectTo === 'onboarding' && children) {
      return <>{children}</>;
    }
  } else {
    // Normal behavior: protecting dashboard from users without profiles
    // Redirect to onboarding if no profile exists
    if (redirectTo === 'onboarding') {
      return <Navigate to="/onboarding" replace />;
    }
    // If user has profile (redirectTo === 'dashboard'), render children
    if (redirectTo === 'dashboard' && children) {
      return <>{children}</>;
    }
  }

  // Fallback loading state (shouldn't normally reach here)
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2,
      }}
    >
      <CircularProgress size={48} />
      <Typography variant="body1" color="text.secondary">
        Determining your profile status...
      </Typography>
    </Box>
  );
}
